import React from 'react';
import * as Collapsible from '@radix-ui/react-collapsible';
import Icon from '@/components/ui/Icon';
import cn from '@/utils/cn';

interface CollapsibleSectionProps {
  children: React.ReactNode;
  headerExtra?: React.ReactNode;
  id?: string;
  open: boolean;
  summary?: React.ReactNode;
  title: string;
  onOpenChange: (open: boolean) => void;
}

// The detail page's raised card and its section title, shared with the Account/Subscriber cards.
export const sectionCardClass = cn(
  'tw-box-border tw-scroll-mt-4 tw-overflow-hidden tw-rounded-lg tw-border tw-border-solid',
  'tw-border-spa-border-secondary tw-bg-spa-bg-secondary',
);

export const sectionTitleClass = 'tw-m-0 tw-text-xl tw-font-semibold tw-leading-7 tw-text-spa-heading';

// A raised section whose whole header is the toggle, so a collapsed section is one big button.
const CollapsibleSection = ({
  children, headerExtra, id, open, summary, title, onOpenChange,
}: CollapsibleSectionProps) => (
  <Collapsible.Root asChild open={open} onOpenChange={onOpenChange}>
    <section className={sectionCardClass} id={id}>
      <h3 className={sectionTitleClass}>
        <Collapsible.Trigger asChild>
          <button
            className={cn(
              'tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-gap-2 tw-border-0 tw-bg-transparent',
              'tw-px-5 tw-py-4 tw-text-left tw-text-xl tw-font-semibold tw-leading-7 tw-text-inherit',
              'tw-transition-colors hover:tw-bg-spa-bg-tertiary',
              'focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-spa-accent',
              'focus-visible:-tw-outline-offset-2',
              // Matches the card's corners so the outline isn't clipped by its overflow-hidden.
              open ? 'tw-rounded-t-lg' : 'tw-rounded-lg',
            )}
            type="button"
          >
            <span
              aria-hidden="true"
              className={cn(
                'tw-inline-flex tw-shrink-0 tw-text-spa-tertiary tw-transition-transform',
                !open && '-tw-rotate-90',
              )}
            >
              <Icon icon="ChevronDown" size={16} />
            </span>
            {title}
            {summary && <span className="tw-text-base tw-font-normal tw-text-spa-secondary">{summary}</span>}
            {headerExtra}
          </button>
        </Collapsible.Trigger>
      </h3>
      <Collapsible.Content
        className="tw-border-0 tw-border-t tw-border-solid tw-border-spa-border-secondary tw-px-5 tw-pb-5 tw-pt-4"
      >
        {children}
      </Collapsible.Content>
    </section>
  </Collapsible.Root>
);

export default CollapsibleSection;
